import { builtInPaperSizes, convertPaperSizeUnits, EPOCH, PaperSize } from "./PaperSize";
import { Corner } from "./Rules";
import { mergeToolbox, Tombstones, ToolTraits } from "./Sync";
import { convert, Units } from "./Units";
import { ViewMode } from "./ChooseOpViewMode";
import { defineStore } from "pinia";
import { toRaw, watch } from "vue";
import { useCurrentUser, syncUserPreferences } from "./CommonGlobals";
import { PERSISTENT_USER } from "./PersistentUser";

/// As stored in localStorage.
export interface UserPreferences {
    /// The schema version for UserPreferences.
    v: 2,
    /// How should we show the toolbox to the user?
    viewMode: ViewMode;
    /// All paper sizes in the same units.
    units: Units;
    /// List of the user's paper sizes.
    paperSizes: PaperSize[];
    /// Paper sizes that have been deleted.
    paperSizesTombstones: Tombstones;
    /// The last time a preference was modified.
    modifiedTimestamp: string;
    /// Space between rules in grid overlay.
    ruleSpacing: number,
    ruleOrigin: Corner,
    /// Append downloaded files with the operation names.
    appendFileNames: boolean,
    /// Append downloaded files with custom names.
    appendCustomNames: boolean,
    customSuffix: string,
    /// Show button to export saved steps to CLI.
    showCLIbutton?: boolean,
    displayCutBlocks: boolean,
    cutBlockColor: string,
    /// Used in desktop only.
    languageCode: string
}

function defaultUserPreferences(units: Units): UserPreferences {
    const preferences: UserPreferences = {
        v: 2,
        viewMode: 'icons',
        units,
        modifiedTimestamp: EPOCH,
        paperSizesTombstones: {},
        paperSizes: builtInPaperSizes(units),
        ruleSpacing: convert(0.25, Units.INCHES, units),
        ruleOrigin: Corner.TopLeft,
        appendFileNames: true,
        appendCustomNames: false,
        customSuffix: "",
        showCLIbutton: false,
        displayCutBlocks: false,
        cutBlockColor: "#FF00FF",
        languageCode: "en"
    };
    return preferences;
}

const USER_PREFERENCES_NAMESPACE = "USER_PREFERENCES";

export function localStorageKeyForUser(uid?: string | null): string {
    return uid ? `${USER_PREFERENCES_NAMESPACE}:${uid}`
        : USER_PREFERENCES_NAMESPACE;
}

function localStorageKeyForCurrentUser(uid: string | null): string {
    return localStorageKeyForUser(uid ?? window.localStorage.getItem(PERSISTENT_USER));
}

function loadUserPreferences(localStorageKey: string): UserPreferences {
    const text = window.localStorage.getItem(localStorageKey);
    if (!text) {
        return defaultUserPreferences(Units.INCHES);
    }
    const userPreferences = JSON.parse(text) as UserPreferences;
    return Object.assign(defaultUserPreferences(userPreferences.units),
        userPreferences);
}

export const nowJSON = () => new Date().toJSON();

export class PaperSizeTraits implements ToolTraits<PaperSize> {
    getEpitaph = (paperSize: PaperSize) => paperSize.name;
    getKey = (paperSize: PaperSize) => paperSize.name;
    getTimestamp = (paperSize: PaperSize) => paperSize.timestamp ?? EPOCH;
}

export function convertPaperSizesToUnits(paperSizes: PaperSize[], oldUnits: Units,
    newUnits: Units): PaperSize[]
{
    return paperSizes.map(paperSize => {
        paperSize = { ...paperSize };
        convertPaperSizeUnits(paperSize, oldUnits,  newUnits);
        return paperSize;
    });
}

interface ConvertiblePreferences {
    units: Units,
    paperSizes: PaperSize[],
    ruleSpacing: number
}

function convertPreferencesToUnits(prefs: ConvertiblePreferences,
    newUnits: Units): ConvertiblePreferences
{
    return {
        units: newUnits,
        paperSizes: convertPaperSizesToUnits(prefs.paperSizes, prefs.units, newUnits),
        ruleSpacing: convert(prefs.ruleSpacing, prefs.units, newUnits)
    };
}

function _useUserPreferences(currentUser: ReturnType<typeof useCurrentUser>) {
    const store = defineStore('userPreferences', {
        state: () => loadUserPreferences(localStorageKeyForCurrentUser(currentUser.id)),
        actions: {
            setUnits(newUnits: Units): void {
                if (newUnits === this.units) {
                    return;
                }
                this.$patch(convertPreferencesToUnits(this.$state, newUnits));
                this.saveAndSync();
            },
            async saveAndSync(): Promise<void> {
                // Avoid dropping any paper sizes that were added in a simultaneously open
                // window.
                const uid = currentUser.id;
                const localStorageKey = localStorageKeyForUser(uid ??
                    window.localStorage.getItem(PERSISTENT_USER));
                const preferencesFromLocalStorage =
                    loadUserPreferences(localStorageKey);
                // Convert the two toolboxes to the same units, and then merge them.
                for (const paperSize of preferencesFromLocalStorage.paperSizes) {
                    convertPaperSizeUnits(paperSize,
                        preferencesFromLocalStorage.units, this.units);
                }
                const merged = mergeToolbox(preferencesFromLocalStorage.paperSizes,
                    preferencesFromLocalStorage.paperSizesTombstones,
                    this.paperSizes, this.paperSizesTombstones, new PaperSizeTraits);
                const mergedPreferences: UserPreferences = {
                    ...toRaw(this.$state),
                    modifiedTimestamp: nowJSON(),
                    paperSizes: merged.tools,
                    paperSizesTombstones: merged.tombstones,
                    v: 2,
                };
                window.localStorage.setItem(localStorageKey,
                    JSON.stringify(mergedPreferences));
                    this.$patch(mergedPreferences);
                if (uid) {
                    await syncUserPreferences(uid, mergedPreferences);
                    this.$patch(mergedPreferences);
                }
            },
            async _onUserIdChanged(uid: string | null): Promise<void> {
                const userKey = localStorageKeyForUser(uid ??
                    window.localStorage.getItem(PERSISTENT_USER));
                if (uid) {
                    // Load the user's preferences.
                    const userJSON = window.localStorage.getItem(userKey);
                    let userPreferences: UserPreferences | undefined;
                    if (userJSON) {
                        userPreferences = JSON.parse(userJSON) as UserPreferences;
                    } else {
                        // User has no preferences in local storage.  Copy the
                        // anonymous preferences into this user's preferences.
                        const anonymousJSON = window.localStorage.getItem(
                            localStorageKeyForUser(null));
                        if (anonymousJSON) {
                            // Reset the anonymous preference's timestamp to zero so
                            // that preferences downloaded from Cloud Storage take
                            // precedence.
                            userPreferences =
                                JSON.parse(anonymousJSON) as UserPreferences;
                            userPreferences.modifiedTimestamp = EPOCH;
                            window.localStorage.setItem(userKey,
                                JSON.stringify(userPreferences));
                        } else {
                            // There are no anonymous preferences either.
                            // Load the defaults.
                            userPreferences = defaultUserPreferences(Units.INCHES);
                        }
                    }
                    this.$patch(userPreferences);
                    await syncUserPreferences(uid, userPreferences);
                    this.$patch(userPreferences);
                } else {
                    this.$patch(loadUserPreferences(userKey));
                }
            }
        }
    });
    return store();
}

type Store = ReturnType<typeof _useUserPreferences>;
let _userPreferences: Store | null = null;
export function useUserPreferences(): Store {
    if (!_userPreferences) {
        const currentUser = useCurrentUser();
        _userPreferences = _useUserPreferences(currentUser);
        watch(() => currentUser.id, id => _userPreferences?._onUserIdChanged(id));
    }
    return _userPreferences;
}
